import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Switch, FormControlLabel, Chip, Checkbox, CircularProgress,
  Alert, Divider, Tooltip, InputAdornment, OutlinedInput, List, ListItem, ListItemText,
  ListItemSecondaryAction, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';
import { db } from '../firebase';
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDocs, query, orderBy, Timestamp,
} from 'firebase/firestore';
import { useBranch } from '../context/BranchContext';
import TabProcedures from '../components/TabProcedures';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EMPTY_ARANCEL = {
  branchPrices: [],
  branchIds: [],
  unidadNegocioId: '',
  unidadNegocioName: '',
  tipoPrestacionId: '',
  tipoPrestacionName: '',
  prestacion: '',
  specialtyId: '',
  activo: true,
};

const formatPrice = (val) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(Number(val) || 0);

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────

const ArancelConfig = () => {
  const { isAdmin, filterByBranch } = useBranch();
  const [tab, setTab] = useState(0);

  // Datos de referencia
  const [branches, setBranches] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [goals, setGoals] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [branchSnap, specSnap, unidadSnap, tipoSnap, arancelSnap, goalsSnap] = await Promise.all([
        getDocs(query(collection(db, 'branches'), orderBy('name'))),
        getDocs(query(collection(db, 'specialties'), orderBy('name'))),
        getDocs(query(collection(db, 'unidadesNegocio'), orderBy('name'))),
        getDocs(query(collection(db, 'tiposPrestacion'), orderBy('name'))),
        getDocs(collection(db, 'aranceles')),
        getDocs(query(collection(db, 'goals'), orderBy('name'))),
      ]);
      setBranches(branchSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSpecialties(specSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setUnidades(unidadSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTipos(tipoSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setGoals(goalsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((g) => g.isActive !== false));
      const arancelList = arancelSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.activo !== false);
      setAranceles(filterByBranch(arancelList).sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es')));
    } catch (e) {
      setError('Error al cargar datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Aranceles
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Gestiona los aranceles de prestaciones por sucursal, unidad de negocio y tipo de prestación
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Aranceles" />
          <Tab label="Unidades de Negocio" />
          <Tab label="Tipos de Prestación" />
          <Tab label="Procedimientos" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tab} index={0}>
              <TabAranceles
                aranceles={aranceles}
                branches={branches}
                unidades={unidades}
                tipos={tipos}
                specialties={specialties}
                onRefresh={loadAll}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <TabCatalogo
                titulo="Unidades de Negocio"
                coleccion="unidadesNegocio"
                items={unidades}
                onRefresh={loadAll}
              />
            </TabPanel>
            <TabPanel value={tab} index={2}>
              <TabTiposPrestacion
                items={tipos}
                goals={goals}
                onRefresh={loadAll}
              />
            </TabPanel>
            <TabPanel value={tab} index={3}>
              <TabProcedures aranceles={aranceles} />
            </TabPanel>
          </>
        )}
      </Paper>
    </Box>
  );
};

// ─── TAB: ARANCELES ──────────────────────────────────────────────────────────

const TabAranceles = ({ aranceles, branches, unidades, tipos, specialties, onRefresh }) => {
  const { isAdmin, accessibleBranches } = useBranch();
  const availableBranches = isAdmin ? branches : accessibleBranches;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ARANCEL);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const arancelesFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return aranceles;
    return aranceles.filter((a) => (a.prestacion || '').toLowerCase().includes(q));
  }, [aranceles, busqueda]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_ARANCEL);
    setFormError('');
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a.id);
    const migratedPrices = Array.isArray(a.branchPrices)
      ? a.branchPrices
      : a.branchId
      ? [{ branchId: a.branchId, branchName: a.branchName || '', price: a.precio || '' }]
      : [];
    setForm({
      branchPrices: migratedPrices,
      branchIds: migratedPrices.map((b) => b.branchId),
      unidadNegocioId: a.unidadNegocioId || '',
      unidadNegocioName: a.unidadNegocioName || '',
      tipoPrestacionId: a.tipoPrestacionId || '',
      tipoPrestacionName: a.tipoPrestacionName || '',
      prestacion: a.prestacion || '',
      specialtyId: a.specialtyId || '',
      activo: a.activo !== false,
    });
    setFormError('');
    setOpen(true);
  };

  const handleBranchSelectionChange = (selectedIds) => {
    setForm((f) => {
      const newPrices = selectedIds.map((id) => {
        const existing = f.branchPrices.find((b) => b.branchId === id);
        const b = branches.find((x) => x.id === id);
        return { branchId: id, branchName: b?.name || '', price: existing ? existing.price : '' };
      });
      return { ...f, branchPrices: newPrices, branchIds: selectedIds };
    });
  };

  const handleBranchPriceChange = (branchId, price) => {
    setForm((f) => ({
      ...f,
      branchPrices: f.branchPrices.map((b) => (b.branchId === branchId ? { ...b, price } : b)),
    }));
  };

  const handleUnidadChange = (unidadNegocioId) => {
    const u = unidades.find((x) => x.id === unidadNegocioId);
    setForm((f) => ({ ...f, unidadNegocioId, unidadNegocioName: u?.name || '' }));
  };

  const handleTipoChange = (tipoPrestacionId) => {
    const t = tipos.find((x) => x.id === tipoPrestacionId);
    setForm((f) => ({ ...f, tipoPrestacionId, tipoPrestacionName: t?.name || '' }));
  };

  const validate = () => {
    if (!form.branchPrices || form.branchPrices.length === 0) return 'Debes seleccionar al menos una sucursal.';
    if (form.branchPrices.some((b) => !b.price || Number(b.price) <= 0)) return 'Cada sucursal seleccionada debe tener un precio mayor a cero.';
    if (!form.unidadNegocioId) return 'Debes seleccionar una unidad de negocio.';
    if (!form.tipoPrestacionId) return 'Debes seleccionar un tipo de prestación.';
    if (!form.prestacion.trim()) return 'El nombre de la prestación es obligatorio.';
    return '';
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true);
    setFormError('');
    try {
      const branchPrices = form.branchPrices.map((b) => ({ ...b, price: Number(b.price) || 0 }));
      const data = {
        ...form,
        branchPrices,
        branchIds: branchPrices.map((b) => b.branchId),
        updatedAt: Timestamp.now(),
      };
      if (editing) {
        await updateDoc(doc(db, 'aranceles', editing), data);
      } else {
        await addDoc(collection(db, 'aranceles'), { ...data, createdAt: Timestamp.now() });
      }
      setOpen(false);
      await onRefresh();
    } catch (e) {
      setFormError('Error al guardar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async (a) => {
    try {
      await updateDoc(doc(db, 'aranceles', a.id), {
        activo: !a.activo,
        updatedAt: Timestamp.now(),
      });
      await onRefresh();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'aranceles', id));
      setDeleteConfirm(null);
      await onRefresh();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre de prestación"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          sx={{ minWidth: 280, flex: '1 1 280px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Nuevo Arancel
        </Button>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>Sucursal</TableCell>
              <TableCell>Unidad de Negocio</TableCell>
              <TableCell>Tipo de Prestación</TableCell>
              <TableCell>Prestación</TableCell>
              <TableCell align="right">Precio</TableCell>
              <TableCell>Agenda Online</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {arancelesFiltrados.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {busqueda.trim()
                    ? 'No hay aranceles que coincidan con la búsqueda.'
                    : 'No hay aranceles registrados. Haz clic en "Nuevo Arancel" para comenzar.'}
                </TableCell>
              </TableRow>
            )}
            {arancelesFiltrados.map((a) => (
              <TableRow key={a.id} hover sx={{ opacity: a.activo === false ? 0.5 : 1 }}>
                <TableCell>
                  {a.branchPrices && a.branchPrices.length
                    ? a.branchPrices.map((b) => b.branchName).join(', ')
                    : a.branchName || '—'}
                </TableCell>
                <TableCell>{a.unidadNegocioName}</TableCell>
                <TableCell>{a.tipoPrestacionName}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{a.prestacion}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {a.branchPrices && a.branchPrices.length
                    ? a.branchPrices.map((b) => `${b.branchName}: ${formatPrice(b.price)}`).join(' · ')
                    : formatPrice(a.precio)}
                </TableCell>
                <TableCell>
                  {a.specialtyId ? (
                    <Chip label="Vinculada" size="small" color="primary" variant="outlined" />
                  ) : (
                    <Chip label="Sin vincular" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={a.activo !== false ? 'Activo' : 'Inactivo'}
                    size="small"
                    color={a.activo !== false ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => openEdit(a)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={a.activo !== false ? 'Desactivar' : 'Activar'}>
                    <IconButton size="small" onClick={() => handleToggleActivo(a)}>
                      <Switch checked={a.activo !== false} size="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirm(a)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Crear / Editar Arancel */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Editar Arancel' : 'Nuevo Arancel'}
        </DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Sucursales</InputLabel>
              <Select
                multiple
                value={form.branchPrices.map((b) => b.branchId)}
                label="Sucursales"
                onChange={(e) => handleBranchSelectionChange(e.target.value)}
                input={<OutlinedInput label="Sucursales" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const b = availableBranches.find((x) => x.id === id);
                      return <Chip key={id} label={b?.name || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {availableBranches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    <Checkbox checked={form.branchPrices.some((x) => x.branchId === b.id)} />
                    <ListItemText primary={b.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.branchPrices.map((bp) => (
              <TextField
                key={bp.branchId}
                label={`Precio - ${branches.find((b) => b.id === bp.branchId)?.name || bp.branchId}`}
                value={bp.price}
                onChange={(e) => handleBranchPriceChange(bp.branchId, e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                helperText="Valor en CLP (pesos chilenos)"
              />
            ))}

            <FormControl fullWidth required>
              <InputLabel>Unidad de Negocio</InputLabel>
              <Select
                value={form.unidadNegocioId}
                label="Unidad de Negocio"
                onChange={(e) => handleUnidadChange(e.target.value)}
              >
                {unidades.filter((u) => u.activo !== false).map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Tipo de Prestación</InputLabel>
              <Select
                value={form.tipoPrestacionId}
                label="Tipo de Prestación"
                onChange={(e) => handleTipoChange(e.target.value)}
              >
                {tipos.filter((t) => t.activo !== false).map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Prestación"
              value={form.prestacion}
              onChange={(e) => setForm((f) => ({ ...f, prestacion: e.target.value }))}
              required
              fullWidth
              placeholder="Ej: Consulta General, Ecografía abdominal..."
            />

            <Divider />

            <FormControl fullWidth>
              <InputLabel>Especialidad (Agendamiento Online)</InputLabel>
              <Select
                value={form.specialtyId}
                label="Especialidad (Agendamiento Online)"
                onChange={(e) => setForm((f) => ({ ...f, specialtyId: e.target.value }))}
              >
                <MenuItem value=""><em>Sin vincular</em></MenuItem>
                {specialties.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Vincula esta prestación a una especialidad del agendamiento online para que el precio se muestre automáticamente al cliente.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={form.activo}
                  onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))}
                />
              }
              label="Arancel activo"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar eliminación */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Eliminar Arancel</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el arancel <strong>{deleteConfirm?.prestacion}</strong>? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── TAB GENÉRICO: CATÁLOGO (Unidades de Negocio / Tipos de Prestación) ────

const TabCatalogo = ({ titulo, coleccion, items, onRefresh }) => {
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, coleccion), {
        name: newName.trim(),
        activo: true,
        createdAt: Timestamp.now(),
      });
      setNewName('');
      await onRefresh();
    } catch (e) {
      setError('Error al guardar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, coleccion, id), {
        name: editName.trim(),
        updatedAt: Timestamp.now(),
      });
      setEditing(null);
      await onRefresh();
    } catch (e) {
      setError('Error al actualizar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      await updateDoc(doc(db, coleccion, item.id), {
        activo: !item.activo,
        updatedAt: Timestamp.now(),
      });
      await onRefresh();
    } catch (e) {
      setError('Error: ' + e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, coleccion, id));
      await onRefresh();
    } catch (e) {
      setError('Error al eliminar: ' + e.message);
    }
  };

  return (
    <Box maxWidth={480}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
        {titulo}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          size="small"
          label={`Nueva ${titulo.endsWith('ción') ? titulo.slice(0, -4) : titulo.replace('s', '')}`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          sx={{ flex: 1 }}
          placeholder={`Nombre de la ${titulo.toLowerCase().endsWith('ión') ? titulo.slice(0, -5).toLowerCase() : titulo.toLowerCase()}...`}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!newName.trim() || saving}
          startIcon={<AddIcon />}
        >
          Agregar
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            No hay registros. Agrega el primero usando el formulario de arriba.
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  {editing === item.id ? (
                    <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                      <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdate(item.id)}
                        sx={{ flex: 1 }}
                        autoFocus
                      />
                      <Button size="small" variant="contained" onClick={() => handleUpdate(item.id)} disabled={saving}>
                        Guardar
                      </Button>
                      <Button size="small" onClick={() => setEditing(null)}>
                        Cancelar
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={item.name}
                        secondary={item.activo === false ? 'Inactivo' : null}
                        sx={{ opacity: item.activo === false ? 0.5 : 1 }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => { setEditing(item.id); setEditName(item.name); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={item.activo === false ? 'Activar' : 'Desactivar'}>
                          <Switch
                            checked={item.activo !== false}
                            size="small"
                            onChange={() => handleToggle(item)}
                          />
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

// ─── TAB: TIPOS DE PRESTACIÓN CON VÍNCULO A META ─────────────────────────────

const TabTiposPrestacion = ({ items, goals, onRefresh }) => {
  const [newName, setNewName] = useState('');
  const [newGoalId, setNewGoalId] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [editGoalId, setEditGoalId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const goalMap = useMemo(() => {
    const map = {};
    for (const g of goals) map[g.id] = g.name || g.id;
    return map;
  }, [goals]);

  const getSelectedGoalName = (id) => goalMap[id] || '';

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const goalName = getSelectedGoalName(newGoalId);
      await addDoc(collection(db, 'tiposPrestacion'), {
        name: newName.trim(),
        activo: true,
        goalId: newGoalId || '',
        goalName: goalName,
        createdAt: Timestamp.now(),
      });
      setNewName('');
      setNewGoalId('');
      await onRefresh();
    } catch (e) {
      setError('Error al guardar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      const goalName = getSelectedGoalName(editGoalId);
      await updateDoc(doc(db, 'tiposPrestacion', id), {
        name: editName.trim(),
        goalId: editGoalId || '',
        goalName: goalName,
        updatedAt: Timestamp.now(),
      });
      setEditing(null);
      await onRefresh();
    } catch (e) {
      setError('Error al actualizar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      await updateDoc(doc(db, 'tiposPrestacion', item.id), {
        activo: !item.activo,
        updatedAt: Timestamp.now(),
      });
      await onRefresh();
    } catch (e) {
      setError('Error: ' + e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'tiposPrestacion', id));
      await onRefresh();
    } catch (e) {
      setError('Error al eliminar: ' + e.message);
    }
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setEditName(item.name);
    setEditGoalId(item.goalId || '');
  };

  return (
    <Box maxWidth={720}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
        Tipos de Prestación
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              fullWidth
              size="small"
              label="Nuevo tipo de prestación"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="Ej: Consulta general"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Meta a la que tributa</InputLabel>
              <Select
                value={newGoalId}
                label="Meta a la que tributa"
                onChange={(e) => setNewGoalId(e.target.value)}
              >
                <MenuItem value=""><em>Sin vincular</em></MenuItem>
                {goals.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAdd}
              disabled={!newName.trim() || saving}
              startIcon={<AddIcon />}
            >
              Agregar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            No hay registros. Agrega el primero usando el formulario de arriba.
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  {editing === item.id ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                      <TextField
                        size="small"
                        label="Nombre"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdate(item.id)}
                        fullWidth
                        autoFocus
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Meta a la que tributa</InputLabel>
                        <Select
                          value={editGoalId}
                          label="Meta a la que tributa"
                          onChange={(e) => setEditGoalId(e.target.value)}
                        >
                          <MenuItem value=""><em>Sin vincular</em></MenuItem>
                          {goals.map((g) => (
                            <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size="small" variant="outlined" onClick={() => setEditing(null)}>Cancelar</Button>
                        <Button size="small" variant="contained" onClick={() => handleUpdate(item.id)} disabled={saving}>
                          Guardar
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={item.name}
                        secondary={
                          <>
                            {item.activo === false ? 'Inactivo · ' : ''}
                            {item.goalId ? `Meta: ${goalMap[item.goalId] || item.goalName || item.goalId}` : 'Sin meta asociada'}
                          </>
                        }
                        sx={{ opacity: item.activo === false ? 0.5 : 1 }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => startEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={item.activo === false ? 'Activar' : 'Desactivar'}>
                          <Switch
                            checked={item.activo !== false}
                            size="small"
                            onChange={() => handleToggle(item)}
                          />
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default ArancelConfig;
